import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { LuChevronDown } from "react-icons/lu";

export const DesignOptionDropdown = ({
  label = "Choose Option",
  options,
  value,
  onChange,
  getOptionTitle,
  getOptionBadge,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const activeIndex =
    Number.isInteger(value) && value >= 0 && value < options.length ? value : 0;

  const activeOption = useMemo(() => options[activeIndex] || null, [activeIndex, options]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }

  const renderOptionBadge = (badge, isActive = false) => {
    if (!badge) return null;

    if (typeof badge === "string") {
      return (
        <span
          className={`inline-flex max-w-full whitespace-normal break-words rounded-full border px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] ${isActive
              ? "border-white/15 bg-white/10 text-white"
              : "border-[#bc1535]/10 bg-[#ffe7ea] text-[#bc1535]"
            }`}
        >
          {badge}
        </span>
      );
    }

    return (
      <div className="flex min-w-0 flex-col gap-1">
        {badge.highlight ? (
          <span
            className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] ${isActive ? "bg-white/12 text-white" : "bg-[#ffe7ea] text-[#bc1535]"
              }`}
          >
            {badge.highlight}
          </span>
        ) : null}
        {badge.priceLabel ? (
          <span
            className={`max-w-full whitespace-normal break-words text-[10px] font-semibold ${isActive ? "text-white/85" : "text-gray-600"
              }`}
          >
            {badge.priceLabel}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="mb-3" ref={containerRef}>
      <div className="flex items-center justify-between gap-3">
        <p className="mb-1 text-[9px] font-semibold uppercase tracking-[0.2em]">
          {label}
        </p>
        <div className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.1em] text-brand-red">
          <span>{options.length} options</span>
        </div>
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen((open) => !open);
          }}
          className="flex w-full items-start justify-between gap-2 rounded-xl bg-white px-2.5 py-2 text-left shadow-[0_6px_18px_rgba(15,15,15,0.05)] transition-colors hover:border-gray-400 sm:items-center sm:py-1.5 border-[1px] border-brand-red"
        >
          <div className="min-w-0 flex-1">
            <div className="mt-0.5 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
              <p className="mb-0 flex items-center text-[11px] font-semibold text-gray-800 sm:truncate">
                {activeOption ? getOptionTitle(activeOption, activeIndex) : ""}
              </p>
              {activeOption ? renderOptionBadge(getOptionBadge(activeOption, activeIndex)) : null}
            </div>
          </div>

          <span
            className={`flex size-5 shrink-0 items-center justify-center rounded-full bg-black text-white transition-transform ${isOpen ? "rotate-180" : ""
              }`}
          >
            <LuChevronDown className="text-xs" />
          </span>
        </button>

        {isOpen ? (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 overflow-hidden rounded-xl border border-black/10 bg-white shadow-[0_16px_34px_rgba(15,15,15,0.14)]">
            <div className="max-h-52 overflow-y-auto py-1">
              {options.map((option, index) => {
                const isActiveOption = index === activeIndex;

                return (
                  <button
                    key={`${option?.name || "option"}-${index}`}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(index);
                      setIsOpen(false);
                    }}
                    className={`flex w-full items-start gap-2 px-2.5 py-2 text-left transition-colors sm:items-center ${isActiveOption
                        ? "bg-brand-black text-white"
                        : "text-gray-800 hover:bg-black/[0.04]"
                      }`}
                  >
                    <span
                      className={`flex size-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] ${isActiveOption
                          ? "border-white/30 bg-white text-brand-black"
                          : "border-black/10 bg-[#f7f3ee] text-transparent"
                        }`}
                    >
                      <FaCheck />
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                        <span className="text-[11px] font-semibold sm:truncate">
                          {getOptionTitle(option, index)}
                        </span>
                        {renderOptionBadge(getOptionBadge(option, index), isActiveOption)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
